import React from 'react';
import PropTypes from 'prop-types';
import { get, omit } from 'lodash';
import { DYNAMIC_CREATIVES_REPORT_VIEW } from 'constants/capabilities';
import { DYNAMIC_CREATIVES_DIMENSION_KEYS } from 'constants/dynamicCreatives';
import ProtectedComponent from 'hoc/ProtectedComponent';
import RenderingTile from 'components/molecules/RenderingTile';
import DynamicCreativesPivotTable from 'components/molecules/tables/DynamicCreativesPivotTable';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

const ProtectedDynamicCreativesReports = ProtectedComponent({
    requiredCapabilities: [DYNAMIC_CREATIVES_REPORT_VIEW]
})((props) => {
    const {
        hasDynamicCreativesData,
        dynamicCreatives,
        dynamicProfiles,
        entityObjective,
        exportName,
        entityObjectiveLabelCode,
    } = props;

    const params = get(dynamicCreatives, 'params');
    const profileNames = get(dynamicCreatives, 'dynamicProfileNames', []);

    // NOTE: We are using total_activities over ecpa_activities at this point since
    // the data currently doesn't differentiate eCPA activities
    const {
        eCPAActivitiesOrClicks,
        eCPAActivitiesOrClicksTerm,
    } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

    const components = profileNames.reduce((prev, profileName, idx) => {
        const data = get(dynamicCreatives, ['data', profileName, 'data'], []);
        const profile = get(dynamicProfiles, [`${profileName}`]);
        const primaryPivot = get(dynamicProfiles, [`${profileName}`, 'pivotPrimary']);
        const dimData = omit(get(dynamicCreatives, ['data', profileName, 'byDimension', primaryPivot], {}), ['(not set)']);
        const dimDataOrdered = Object.entries(dimData).sort((a, b) => (b[1].impressions - a[1].impressions));
        const dimDataKeys = [];
        const dimDataValues = [];
        dimDataOrdered.forEach((item) => {
            dimDataKeys.push(item[0]);
            dimDataValues.push(item[1]);
        });

        const omittedKeys = DYNAMIC_CREATIVES_DIMENSION_KEYS.filter(e => !get(profile, ['meta', e, 'showOnReport']));

        const filteredData = data.filter(row => row[primaryPivot] !== '(not set)');
        const hasData = hasDynamicCreativesData === null ? hasDynamicCreativesData : filteredData.length > 0;

        prev.push(hasData ? (
            <TwoAxisBarWithScatterChart
                key={`bar-and-scatter-${idx + 1}`}
                exportName={`${exportName}-bar-and-scatter-impressions-vs-kpi-${idx + 1}`}
                hasDynamicCreativesData={hasDynamicCreativesData}
                title={`DYNAMIC CREATIVE - TOP IMPRESSIONS VS. ${eCPAActivitiesOrClicksTerm.toUpperCase()} - ${profileName}`}
                data={dimDataValues}
                showTopOnly
                yBar="impressions"
                scatter={eCPAActivitiesOrClicks}
                scatterTitle={eCPAActivitiesOrClicksTerm}
                categories={dimDataKeys}
                animateDuration={2000}
            />
        ) : (
            <RenderingTile height='400px' show={hasData} key={`bar-and-scatter-${idx + 1}`} />
        ));

        prev.push(hasData ? (
            <DynamicCreativesPivotTable
                key={`pivot-table-${idx + 1}`}
                exportName={`${exportName}-pivot-table-${idx + 1}`}
                profileName={profileName}
                profile={profile}
                hasDynamicCreativesData={hasDynamicCreativesData}
                dynamicCreativesData={filteredData.map(r => omit(r, omittedKeys))}
                dynamicCreativesParams={params}
                entityObjectiveLabelCode={entityObjectiveLabelCode}
                accessors={entityObjective === 'ctr' ? ['impressions', 'clicks', 'ctr'] : undefined}
            />
        ) : (
            <RenderingTile height='400px' show={hasData} key={`pivot-table-${idx + 1}`} />
        ));
        return prev;
    }, []);
    return (
        <React.Fragment>
            {components}
        </React.Fragment>
    );
});

ProtectedDynamicCreativesReports.propTypes = {
    exportName: PropTypes.string.isRequired,
    hasDynamicCreativesData: PropTypes.bool,
    dynamicCreatives: PropTypes.shape({
        params: PropTypes.object,
        data: PropTypes.object
    }),
    dynamicProfiles: PropTypes.object,
    entityObjectiveLabelCode: PropTypes.string,
};

export default ProtectedDynamicCreativesReports;



// WEBPACK FOOTER //
// ./src/components/organisms/ProtectedDynamicCreativesReports.js